/** URL pattern matching for tracking rules, script side.
 *
 * The normalization and matching themselves live in url-match-core.ts, ONE
 * module shared with the server-side matcher (WebsiteRepo), so the two can
 * never drift apart again. This file adds only what needs the script's
 * global state: picking the matching rules out of the loaded config.
 */

import { state } from "./state.js";
import type { TrackingRule } from "./types.js";
import { matchUrl, normalizeUrl } from "./url-match-core.js";

export { matchUrl, normalizeUrl };

/** Find ALL matching pageview rules for a URL (deduplicated by eventName). */
export function findMatchingRules(url: string): TrackingRule[] {
  if (!state.config || !state.config.rules) return [];

  const pageviewRules = state.config.rules.filter(
    (rule) => !rule.triggerType || rule.triggerType === "pageview",
  );

  // Sort by priority: exact > starts_with > contains > wildcard
  const priorityOrder: Record<string, number> = {
    exact: 0,
    starts_with: 1,
    contains: 2,
    wildcard: 3,
  };
  const sortedRules = pageviewRules.slice().sort(
    (a, b) => (priorityOrder[a.matchType || ""] ?? 4) - (priorityOrder[b.matchType || ""] ?? 4),
  );

  const matched: TrackingRule[] = [];
  const seenEvents = new Set<string>();
  for (const rule of sortedRules) {
    if (matchUrl(url, rule.urlPattern, rule.matchType)) {
      if (!seenEvents.has(rule.eventName)) {
        seenEvents.add(rule.eventName);
        matched.push(rule);
      }
    }
  }

  return matched;
}

/** Legacy single-match — used for click/form rules. */
export function findMatchingRule(url: string): TrackingRule | null {
  const rules = findMatchingRules(url);
  if (rules.length === 0) return null;
  return rules[0];
}
